/**
 * Global, class-driven interactions rebuilt from Webflow IX2 in GSAP.
 *
 * Mounted once per page (inside the layout). It wires the interactions that
 * Webflow applied by class across many elements: scroll-into-view reveals,
 * hover effects, and the generic page-load reveal. Component-specific motion
 * (hero, sliders, marquee, parallax) lives in those components.
 *
 * Reveals use `fromTo` with explicit destinations so the pre-paint hide
 * (`html.is-loading`, see animations.css) can be lifted the moment the from
 * states are committed inline, with no flash.
 */
import type { FC } from 'react'

import { useEffect } from 'react'

import { gsap, ScrollTrigger } from '@/lib/gsap'

interface SiteAnimationsProps {
    isHome?: boolean
}

const FADE_UP = [
    '.fade-in-move-on-scroll',
    '.product-card',
    '.category-item',
    '.sale-circle-container',
    '.tab-link-content',
    '.testimonial-holder',
    '.question-card',
    '.category-small-container.no-margins'
].join(', ')

const ZOOM = '.fade-in-slace-on-scroll, .philosophy-card, .blog-main-image'
const BORDER = '.blog-border-container, .author-border-container'
const SLIDE = '.slide-in-bottom'

export const SiteAnimations: FC<SiteAnimationsProps> = ({ isHome = false }) => {
    useEffect(() => {
        const html = document.documentElement
        const cleanups: Array<() => void> = []

        const ctx = gsap.context(() => {
            if (!isHome) {
                const tl = gsap.timeline({ defaults: { ease: 'power1.inOut' } })

                ;['.animation-on-load-01', '.animation-on-load-02', '.animation-on-load-03', '.animation-on-load-04'].forEach(
                    (selector, index) => {
                        if (document.querySelector(selector)) {
                            tl.fromTo(
                                selector,
                                { opacity: 0, y: 30 },
                                { opacity: 1, y: 0, duration: 1 },
                                index * 0.15
                            )
                        }
                    }
                )

                if (document.querySelector('.fixed-navbar')) {
                    tl.fromTo('.fixed-navbar', { opacity: 0 }, { opacity: 1, duration: 1.6 }, 0.3)
                }

                if (document.querySelector('.contact-hero-image')) {
                    tl.fromTo(
                        '.contact-hero-image',
                        { opacity: 0, xPercent: 100 },
                        { opacity: 1, xPercent: 0, duration: 1 },
                        0.45
                    )
                }
            }

            gsap.utils.toArray<HTMLElement>(FADE_UP).forEach((el) => {
                gsap.fromTo(
                    el,
                    { opacity: 0, y: 25 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        delay: 0.1,
                        ease: 'power1.inOut',
                        scrollTrigger: { trigger: el, start: 'top 100%', once: true }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>(ZOOM).forEach((el) => {
                gsap.fromTo(
                    el,
                    { opacity: 0, scale: 1.1 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 1,
                        delay: 0.1,
                        ease: 'power1.inOut',
                        scrollTrigger: { trigger: el, start: 'top 100%', once: true }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>(BORDER).forEach((el) => {
                gsap.fromTo(
                    el,
                    { borderColor: 'rgba(191,140,78,0)' },
                    {
                        borderColor: 'rgba(191,140,78,1)',
                        duration: 0.6,
                        delay: 0.2,
                        ease: 'power1.inOut',
                        scrollTrigger: { trigger: el, start: 'top 100%', once: true }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>(SLIDE).forEach((el) => {
                gsap.fromTo(
                    el,
                    { opacity: 0, y: 100 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: 'power2.out',
                        scrollTrigger: { trigger: el, start: 'top 100%', once: true }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>('.product-card-image-holder').forEach((holder) => {
                const secondary = holder.querySelector('.secondary-product-card-image')
                if (!secondary) return

                gsap.set(secondary, { opacity: 0 })
                const enter = () => gsap.to(secondary, { opacity: 1, duration: 0.2, ease: 'power1.inOut' })
                const leave = () => gsap.to(secondary, { opacity: 0, duration: 0.2, ease: 'power1.inOut' })
                holder.addEventListener('mouseenter', enter)
                holder.addEventListener('mouseleave', leave)
                cleanups.push(() => {
                    holder.removeEventListener('mouseenter', enter)
                    holder.removeEventListener('mouseleave', leave)
                })
            })

            gsap.utils.toArray<HTMLElement>('.category-card-container').forEach((card) => {
                const text = card.querySelector('.category-text')
                const overlay = card.querySelector('.category-overlay')
                const image = card.querySelector('.category-image')

                gsap.set(text, { opacity: 0.69 })
                gsap.set(overlay, { opacity: 1 })
                gsap.set(image, { filter: 'grayscale(100%)' })

                const enter = () => {
                    gsap.to(text, { opacity: 1, duration: 0.8 })
                    gsap.to(overlay, { opacity: 0, duration: 0.8 })
                    gsap.to(image, { filter: 'grayscale(0%)', duration: 0.8 })
                }
                const leave = () => {
                    gsap.to(text, { opacity: 0.69, duration: 0.8 })
                    gsap.to(overlay, { opacity: 1, duration: 0.8 })
                    gsap.to(image, { filter: 'grayscale(100%)', duration: 0.8 })
                }
                card.addEventListener('mouseenter', enter)
                card.addEventListener('mouseleave', leave)
                cleanups.push(() => {
                    card.removeEventListener('mouseenter', enter)
                    card.removeEventListener('mouseleave', leave)
                })
            })

            gsap.utils.toArray<HTMLElement>('.nav-link').forEach((link) => {
                const image = link.querySelector('.nav-menu-image-holder')
                if (!image) return

                gsap.set(image, { opacity: 0 })
                const enter = () => gsap.to(image, { opacity: 1, duration: 0.7 })
                const leave = () => gsap.to(image, { opacity: 0, duration: 0.7 })
                link.addEventListener('mouseenter', enter)
                link.addEventListener('mouseleave', leave)
                cleanups.push(() => {
                    link.removeEventListener('mouseenter', enter)
                    link.removeEventListener('mouseleave', leave)
                })
            })
        }, document.body)

        html.classList.remove('is-loading')
        ScrollTrigger.refresh()

        const onLoad = () => ScrollTrigger.refresh()
        window.addEventListener('load', onLoad)

        return () => {
            window.removeEventListener('load', onLoad)
            cleanups.forEach((fn) => fn())
            ctx.revert()
        }
    }, [isHome])

    return null
}
